// Buchungsregeln (Karenzzeit, frühester Termin, Urlaubssperre) für die
// öffentliche Website.
//
// Der Banner steht auf jeder Seite, der Wizard und das SmartAI-Panel fragen
// dieselben Regeln — deshalb ein Modul-Cache statt drei paralleler Requests.
// Der Cache lebt so lange wie der Tab; die Praxis stellt einen Urlaub nicht
// im Minutentakt um, und ein Reload holt ohnehin frische Werte.

import { useEffect, useState } from 'react';
import { getBookingRules, BookingRules } from '../services/bookingPublicApi';

type Key = string;

const cache = new Map<Key, BookingRules | null>();
const inflight = new Map<Key, Promise<BookingRules | null>>();

const keyOf = (procedureId?: number, procedureKey?: string): Key =>
  `${procedureId ?? ''}|${procedureKey ?? ''}`;

function load(procedureId?: number, procedureKey?: string): Promise<BookingRules | null> {
  const key = keyOf(procedureId, procedureKey);
  if (cache.has(key)) return Promise.resolve(cache.get(key) ?? null);

  const running = inflight.get(key);
  if (running) return running;

  const p = getBookingRules({ procedureId, procedureKey })
    .then(rules => {
      // Ein fehlgeschlagener Abruf wird NICHT gecacht — beim nächsten Mount
      // darf es erneut versuchen, statt den Hinweis dauerhaft zu verschlucken.
      if (rules) cache.set(key, rules);
      return rules;
    })
    .finally(() => { inflight.delete(key); });

  inflight.set(key, p);
  return p;
}

/**
 * @param procedureId  optional — ohne kommen die Praxis-Defaults (reicht für
 *                     den Banner; der Wizard fragt pro Behandlung nach, weil
 *                     Karenzzeit und Endzeit-Grenze daran hängen können).
 */
export function useBookingRules(procedureId?: number, procedureKey?: string) {
  const [rules, setRules] = useState<BookingRules | null>(
    () => cache.get(keyOf(procedureId, procedureKey)) ?? null,
  );
  const [loading, setLoading] = useState(!cache.has(keyOf(procedureId, procedureKey)));

  useEffect(() => {
    let alive = true;
    const key = keyOf(procedureId, procedureKey);
    if (cache.has(key)) {
      setRules(cache.get(key) ?? null);
      setLoading(false);
      return;
    }
    setLoading(true);
    load(procedureId, procedureKey).then(r => {
      if (!alive) return;
      setRules(r);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [procedureId, procedureKey]);

  return { rules, loading };
}

/** Nur für Tests: den Modul-Cache leeren. */
export function __resetBookingRulesCache() {
  cache.clear();
  inflight.clear();
}
